import { useFormikContext } from 'formik'
import { FC, useCallback, useMemo, useState } from 'react'
import { useDebounce } from 'react-use'
import { computeAddressSummary } from '../../../../misc/address.utilities'
import { DEFAULT_ADDRESS } from '../../../../model/Address'
import { Contact, ContactOrganization, MssEmail } from '../../../../model/Contact'
import { SelectOption } from '../../../../model/SelectOption'
import { User } from '../../../../model/User'
import { ContactOption } from '../../../contact'
import { IdentityCard, SearchInput, SelectInput } from '../../../shared'
import { PublicContactSearchModalProps } from './PublicContactSearchModal.model'
import { ValidationModal } from '../../../shared/modal/ValidationModal/ValidationModal.component'
import { useGetProfessions } from '../../../../hooks/queries/professions'
import { Filters } from '../../../../model/Filters'
import { useGetInfiniteContacts } from '../../../../hooks/queries/contact'

export const PublicContactSearchModal: FC<PublicContactSearchModalProps> = ({
  isUnmounting,
  onClose,
  onSubmit,
}) => {
  const { query: professionQuery } = useGetProfessions()

  const [contactSearch, setContactSearch] = useState('')
  const [filters, setFilters] = useState<Filters>()
  const [selectedContact, setSelectedContact] = useState<Contact>()
  const [selectedOrganization, setSelectedOrganization] = useState<ContactOrganization>()
  const [selectedMssEmail, setSelectedMssEmail] = useState<MssEmail>()

  const { setValues } = useFormikContext<User>()

  const {
    query: { isLoading },
    contactsList,
    cancelPendingQuery,
  } = useGetInfiniteContacts({
    enabled: filters?.search?.length > 1,
    filters,
  })

  useDebounce(
    () => {
      if (contactSearch.length > 1) {
        setFilters({
          search: contactSearch,
          private: false,
        })
      } else {
        isLoading && cancelPendingQuery()
      }
    },
    500,
    [contactSearch],
  )

  const organizationOptions: SelectOption<ContactOrganization>[] = useMemo(() => {
    if (selectedContact?.organizations) {
      return selectedContact.organizations.map((orga) => ({
        value: orga,
        label: computeAddressSummary(orga.address, false) ?? 'Adresse vide',
      }))
    } else {
      return []
    }
  }, [selectedContact?.organizations])

  const mssEmailOptions: SelectOption<MssEmail>[] = useMemo(() => {
    if (selectedContact?.mssEmails) {
      return selectedContact.mssEmails.map((mssEmail) => ({
        value: mssEmail,
        label: mssEmail.emailAddress || 'Adresse MSS vide',
      }))
    } else {
      return []
    }
  }, [selectedContact?.mssEmails])

  const selectedOrganizationOption = useMemo(
    () => organizationOptions.find(({ value }) => value.id === selectedOrganization?.id),
    [organizationOptions, selectedOrganization?.id],
  )
  const selectedMssEmailOption = useMemo(
    () => mssEmailOptions.find(({ value }) => value.id === selectedMssEmail?.id),
    [mssEmailOptions, selectedMssEmail?.id],
  )

  const handleSelectContact = useCallback((selected: Contact) => {
    setSelectedContact(selected)
    setSelectedOrganization(selected.organizations.at(0) ?? undefined)
    setContactSearch('')
    setSelectedMssEmail(undefined)
  }, [])

  const handleSubmit = useCallback(() => {
    if (selectedContact) {
      const { firstName, familyName, ppIdentifier } = selectedContact
      const contactAddress = selectedOrganization?.address

      const profession = professionQuery.data?.find(
        (profession) => profession.id === selectedContact.profession?.id,
      )

      const address = contactAddress
        ? {
            street: contactAddress.street,
            street2: contactAddress.street2,
            zipCode: contactAddress.zipCode,
            city: contactAddress.city,
            country: contactAddress.country,
          }
        : DEFAULT_ADDRESS

      setValues((previous) => ({
        ...previous,
        firstName,
        familyName,
        practiceLocation: {
          name: '',
          address: address,
        },
        mailizEmailAddress: selectedMssEmail?.emailAddress ?? '',
        rppsNumber: ppIdentifier ?? '',
        ...(profession && { profession }),
      }))

      setSelectedContact(undefined)
      setSelectedOrganization(undefined)
      setSelectedMssEmail(undefined)
      onSubmit()
    }
  }, [
    onSubmit,
    professionQuery.data,
    selectedContact,
    selectedOrganization?.address,
    selectedMssEmail?.emailAddress,
    setValues,
  ])

  const showContacts = useMemo(
    () => contactSearch.length > 1 && contactSearch === filters?.search,
    [filters?.search, contactSearch],
  )

  return (
    <ValidationModal
      isUnmounting={isUnmounting}
      icon="documentText"
      size="medium"
      title="Pré-remplir le formulaire avec l'Annuaire Santé"
      onSubmit={handleSubmit}
      onClose={onClose}
      testId="modal-contact-health_directory-prefill"
    >
      <div className="w-full text-left space-y-4">
        <SearchInput
          value={contactSearch}
          onChange={(event) => setContactSearch(event.target.value)}
          placeholder="Rechercher un praticien"
          onSelect={handleSelectContact}
          results={showContacts ? contactsList : []}
          renderResult={(option, isHovered) => (
            <ContactOption contact={option} selected={isHovered} />
          )}
          testId="input-public_contact-search"
        />
        {selectedContact && (
          <>
            <IdentityCard
              title={`${selectedContact.firstName} ${selectedContact.familyName}`}
              subtitle={selectedContact.profession?.description ?? ''}
              onRemove={() => setSelectedContact(undefined)}
            />
            <SelectInput
              title="Adresse à utiliser"
              value={selectedOrganizationOption}
              options={organizationOptions}
              onSelect={({ value }) => setSelectedOrganization(value)}
              testId="user-public_contact-address"
            />
            <SelectInput
              title="Adresse MSS à utiliser"
              value={selectedMssEmailOption}
              options={mssEmailOptions}
              onSelect={({ value }) => setSelectedMssEmail(value)}
              testId="user-public_contact-mss_email"
            />
          </>
        )}
      </div>
    </ValidationModal>
  )
}
